/* eslint-disable react/prop-types */
import React from 'react'
import { Form } from 'react-bootstrap'
import { components } from 'react-select'

export const DEPTH_1 = 1

// Clones child components. Note: ModalBody should always use a depth of 1. Any of the child components that require deeper branches should build them out themselves.
export const cloneChildComponents = (props, children, depth) => (
  <>
    {/* eslint-disable-next-line consistent-return */}
    {React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        if (child?.props?.children && depth !== DEPTH_1) {
          return (
            <>
              {React.cloneElement(child, props)}
              {cloneChildComponents(props, child.props.children)}
            </>
          )
        }
        return <>{React.cloneElement(child, props)}</>
      }
    })}
  </>
)

// Formats the data for the domain Select component
export const formatDomainOptions = (employees, setDomains, formatOptions) => {
  let domains = []

  // eslint-disable-next-line no-restricted-syntax
  for (const employee of employees) {
    if (!employee.ignored) {
      const domain = employee.email.split('@')[1]
      if (!formatOptions?.adminDomainsOnly || (formatOptions?.adminDomainsOnly && employee.isAdmin)) {
        domains.push({
          label: domain,
          value: domain,
        })
      }
    }
  }

  domains = domains.filter((v, i, a) => a.findIndex((t) => t.label === v.label) === i) // Remove duplicates
  setDomains(domains)
}

// Formats the data for the employee Select component
export const formatEmployeeOptions = (employees, intl, setEmployeeOptions) => {
  const options = []

  // eslint-disable-next-line no-restricted-syntax
  for (const employee of employees) {
    if (employee.firstName && employee.lastName && !employee.ignored) {
      options.push({
        value: employee,
        label: `${employee.firstName} ${employee.lastName}`,
        email: employee.email,
      })
    }
  }

  // Sort by name
  options.sort((a, b) => a.label.localeCompare(b.label))

  const updatedOptions = options
  // Add "Find an Employee" button as initial option. If selected, the checkSelection function will be triggered in the integration manager and open the modal
  updatedOptions.unshift({
    value: '-',
    label: intl.formatMessage({
      id: 'ENGAGE.FIND_AN_EMPLOYEE',
    }),
  })

  setEmployeeOptions(updatedOptions)
}

// Option component override for the react-select Select component (formats the display of the options)
export function Option({ children, ...props }) {
  const { isSelected, label, value, isMulti, data, displayCount, optionType } = props

  const onClick = (e) => {
    props.selectOption({ ...data })
    e.stopPropagation()
    e.preventDefault()
  }

  // Selector component is a multi-select
  if (isMulti) {
    return (
      <components.Option {...props}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className="multi-select-option" onClick={onClick}>
          <Form.Check readOnly className="multi-select-check" checked={isSelected} label={label} value={value} />
          {
            // Show the count of an option
            displayCount && <span className="multi-select-count">{props.data?.count}</span>
          }
        </div>
      </components.Option>
    )
  }
  // Selector component is a single-select
  return (
    <components.Option {...props}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="select-option" onClick={onClick} title={props.data?.label}>
        {props.data?.label}

        {/* Employee optionType */}
        {optionType === 'employee' && props.data?.email && (
          <span className="ps-2 font-color-1 nowrap">({props.data?.email})</span>
        )}
      </div>
    </components.Option>
  )
}

// Handles selecting an employee from the "Find an Employee" modal
export const selectEmployee = (values, selectedEmployeeReference) => {
  if (selectedEmployeeReference) {
    // eslint-disable-next-line no-unused-expressions
    selectedEmployeeReference && selectedEmployeeReference.current.select.onChange(values.employee)
  }
}

// Check employee selection in Select component, show "Find an Employee" modal if the "Find an Employee" option is selected
export const checkSelection = (employee, setShowFindEmployeeModal, selectedEmployeeReference) => {
  if (employee?.value === '-') {
    setShowFindEmployeeModal(true) // Tell integrationManager to show "Find an Employee" modal
    if (selectedEmployeeReference) {
      // eslint-disable-next-line no-unused-expressions
      selectedEmployeeReference && selectedEmployeeReference.current.select.clearValue()
    }
  }
}

// Returns an array of unique objects representing a single group with fields of id and name
export const getAllGroupsForCustomer = (employees, key = 'GROUPS') => {
  const allGroups = []

  if (employees?.length) {
    // eslint-disable-next-line no-restricted-syntax
    for (const employee of employees) {
      switch (key) {
        case 'GROUPS':
          // eslint-disable-next-line no-case-declarations
          const m365Groups = employee?.m365Groups || []
          // eslint-disable-next-line no-restricted-syntax
          for (const group of m365Groups) {
            // m365Groups
            if (!allGroups.some((groupObject) => groupObject.id === group.id)) {
              allGroups.push({
                id: group.id,
                name: group.name,
              })
            }
          }
          break

        case 'DISTRIBUTION_GROUPS':
          // eslint-disable-next-line no-case-declarations
          const distributionGroups = employee?.distributionGroups || []
          // eslint-disable-next-line no-restricted-syntax
          for (const group of distributionGroups) {
            if (!allGroups.some((groupObject) => groupObject.id === group.id)) {
              allGroups.push({
                id: group.id,
                name: group.name,
              })
            }
          }
          break

        case 'SHARED_MAILBOXES':
          // eslint-disable-next-line no-case-declarations
          const sharedMailboxes = employee?.sharedMailboxes || []
          // eslint-disable-next-line no-restricted-syntax
          for (const group of sharedMailboxes) {
            if (!allGroups.some((groupObject) => groupObject.id === group.id)) {
              allGroups.push({
                id: group.id,
                name: group.name,
              })
            }
          }
          break

        case 'SECURITY_GROUPS':
          // eslint-disable-next-line no-case-declarations
          const securityGroups = employee?.securityGroups || []
          // eslint-disable-next-line no-restricted-syntax
          for (const group of securityGroups) {
            if (!allGroups.some((groupObject) => groupObject.id === group.id)) {
              allGroups.push({
                id: group.id,
                name: group.name,
              })
            }
          }
          break

        default:
          break
      }
    }
  }

  return allGroups
}
